<template>
  <DetailViewModal
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="note?.title || 'Note'"
    :subtitle="noteSubtitle"
    icon="file-text"
    icon-class="icon-note"
    size="xl"
    :loading="loading"
    :created-at="note?.createdAt"
    :updated-at="note?.updatedAt"
    :on-edit="onEdit"
    :item-id="note?.id"
    @close="$emit('close')"
  >
    <div v-if="note" class="note-view">
      <!-- Metadata Bar -->
      <div class="metadata-bar">
        <div class="metadata-item">
          <span class="metadata-label">Type</span>
          <span :class="['badge', getTypeBadge(note.note_type || note.type)]">
            {{ formatType(note.note_type || note.type) }}
          </span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Status</span>
          <span :class="['status-badge', note.status === 'published' ? 'status-published' : 'status-draft']">
            {{ note.status || 'Draft' }}
          </span>
        </div>
        <div v-if="note.isAiGenerated" class="metadata-item">
          <span class="badge bg-purple">
            <vue-feather type="cpu" size="12" />
            AI Generated
          </span>
        </div>
      </div>

      <!-- AI Translate Section -->
      <div class="translate-bar">
        <div class="translate-bar-left">
          <vue-feather type="globe" size="16" />
          <label class="translate-label">View in:</label>
          <select v-model="selectedLanguage" class="translate-select" @change="onLanguageChange">
            <option value="">Original</option>
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </div>
        <button v-if="selectedLanguage && !translatedContent" type="button" @click="fetchTranslation" class="translate-fetch-btn" :disabled="translating">
          <vue-feather :type="translating ? 'loader' : 'globe'" size="14" :class="{ 'spin': translating }" />
          {{ translating ? 'Translating...' : 'AI Translate' }}
        </button>
      </div>

      <!-- Translation Not Available Notice -->
      <div v-if="selectedLanguage && !translatedContent && !translating" class="translation-notice">
        <vue-feather type="info" size="16" />
        <span>Translation for this content is not available in {{ selectedLanguageLabel }}. Click "AI Translate" to generate one.</span>
      </div>

      <!-- Translating Indicator -->
      <div v-if="translating" class="translation-loading">
        <vue-feather type="loader" size="16" class="spin" />
        <span>Translating to {{ selectedLanguageLabel }}...</span>
      </div>

      <!-- Note Content -->
      <div class="content-section main-content">
        <div v-if="selectedLanguage && translatedContent" class="section-header" style="margin-bottom: 0.5rem;">
          <span class="translated-badge">
            <vue-feather type="globe" size="12" /> {{ selectedLanguageLabel }}
          </span>
        </div>
        <KatexContent :content="displayContent" />
      </div>

      <!-- Assignment Info -->
      <div v-if="hasAssignment" class="content-section assignment-section">
        <div class="section-header">
          <vue-feather type="link" size="18" />
          <h4>Assignment</h4>
        </div>
        <div class="assignment-tags">
          <span v-if="note.examName" class="assignment-tag tag-exam">
            <vue-feather type="book-open" size="12" />
            {{ note.examName }}
          </span>
          <span v-if="note.subjectName" class="assignment-tag tag-subject">
            <vue-feather type="layers" size="12" />
            {{ note.subjectName }}
          </span>
          <span v-if="note.topicName" class="assignment-tag tag-topic">
            <vue-feather type="bookmark" size="12" />
            {{ note.topicName }}
          </span>
          <span v-if="note.lessonName" class="assignment-tag tag-lesson">
            <vue-feather type="file-text" size="12" />
            {{ note.lessonName }}
          </span>
        </div>
      </div>

      <!-- Statistics (if available) -->
      <div v-if="hasStats" class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">
            <vue-feather type="eye" size="20" />
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ note.views || 0 }}</div>
            <div class="stat-label">Views</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <vue-feather type="bookmark" size="20" />
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ note.bookmarks || 0 }}</div>
            <div class="stat-label">Bookmarks</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <vue-feather type="share-2" size="20" />
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ note.shares || 0 }}</div>
            <div class="stat-label">Shares</div>
          </div>
        </div>
      </div>
    </div>
  </DetailViewModal>
</template>

<script>
import { computed, ref, watch } from 'vue'
import DetailViewModal from './DetailViewModal.vue'
import KatexContent from '@/components/content/KatexContent.vue'
import api from '@/services/api'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'NoteViewModal',
  components: { DetailViewModal, KatexContent },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    note: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    onEdit: {
      type: Function,
      default: null
    }
  },
  emits: ['update:modelValue', 'close'],
  setup(props) {
    // Translation state
    const selectedLanguage = ref('')
    const translating = ref(false)
    const translatedContent = ref(null)

    const selectedLanguageLabel = computed(() => {
      const lang = languageOptions.find(l => l.code === selectedLanguage.value)
      return lang?.label || selectedLanguage.value
    })

    watch(() => props.note, () => {
      selectedLanguage.value = ''
      translatedContent.value = null
    })

    watch(() => props.modelValue, (val) => {
      if (!val) {
        selectedLanguage.value = ''
        translatedContent.value = null
      }
    })

    const onLanguageChange = async () => {
      translatedContent.value = null
      if (!selectedLanguage.value || !props.note?.id) return
      translating.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'note', content_id: props.note.id, language_code: selectedLanguage.value }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          const parsed = {}
          if (td.title) parsed.title = td.title
          if (td.content) parsed.content = td.content
          if (parsed.title || parsed.content) {
            translatedContent.value = parsed
          }
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        translating.value = false
      }
    }

    const fetchTranslation = async () => {
      if (!selectedLanguage.value || !props.note) return
      translating.value = true
      try {
        const fieldsToTranslate = {}
        if (props.note.title) fieldsToTranslate.title = props.note.title
        if (props.note.content) fieldsToTranslate.content = props.note.content

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational note content to ${selectedLanguageLabel.value}. Keep mathematical formulas, LaTeX expressions, Markdown formatting, and technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })

        let aiData = response.data?.structured_data?.data || {}
        if (!aiData.title && !aiData.content) {
          const responseText = response.data?.response || response.data?.message || ''
          const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
          if (jsonMatch) { try { aiData = JSON.parse(jsonMatch[1].trim()) } catch (e) {} }
          if (!aiData.title && !aiData.content) { try { aiData = JSON.parse(responseText) } catch (e) {} }
        }

        if (aiData.title || aiData.content) {
          translatedContent.value = aiData
        }
      } catch (error) {
        console.error('Translation failed:', error)
      } finally {
        translating.value = false
      }
    }

    const displayContent = computed(() => {
      if (selectedLanguage.value && translatedContent.value?.content) {
        return translatedContent.value.content
      }
      return props.note?.content || ''
    })

    const noteSubtitle = computed(() => {
      const parts = []
      if (props.note?.topicName) parts.push(props.note.topicName)
      if (props.note?.lessonName) parts.push(props.note.lessonName)
      return parts.join(' • ')
    })

    const hasAssignment = computed(() => {
      return props.note?.examName || 
             props.note?.subjectName || 
             props.note?.topicName || 
             props.note?.lessonName
    })

    const hasStats = computed(() => {
      return props.note?.views !== undefined || 
             props.note?.bookmarks !== undefined || 
             props.note?.shares !== undefined
    })

    const formatType = (type) => {
      if (!type) return '-'
      const types = { 
        study_notes: 'Study Notes', 
        topic_content: 'Topic Content', 
        lesson_content: 'Lesson Content' 
      }
      return types[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getTypeBadge = (type) => {
      const badges = { 
        study_notes: 'bg-info', 
        topic_content: 'bg-primary', 
        lesson_content: 'bg-warning' 
      }
      return badges[type] || 'bg-secondary'
    }

    return {
      languageOptions,
      selectedLanguage,
      translating,
      translatedContent,
      selectedLanguageLabel,
      onLanguageChange,
      fetchTranslation,
      displayContent,
      noteSubtitle,
      hasAssignment,
      hasStats,
      formatType,
      getTypeBadge
    }
  }
}
</script>

<style scoped>
.note-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Metadata Bar */
.metadata-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 12px;
  border: 1px solid #a7f3d0;
}

/* Translate Bar */
.translate-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.translate-bar-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0369a1;
}

.translate-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0369a1;
}

.translate-select {
  padding: 0.375rem 0.625rem;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 0.8125rem;
  background: white;
  cursor: pointer;
  min-width: 140px;
}

.translate-select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

.translate-fetch-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.translate-fetch-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.translate-fetch-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.translated-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.translation-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fbbf24;
  color: #92400e;
  font-size: 0.8125rem;
}

.translation-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #e0f2fe;
  border-radius: 8px;
  border: 1px solid #7dd3fc;
  color: #0369a1;
  font-size: 0.8125rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metadata-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.bg-primary { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; }
.bg-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); color: white; }
.bg-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; }
.bg-secondary { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: white; }
.bg-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; }

.status-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-published {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-draft {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

/* Content Section */
.content-section {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.main-content {
  background: white;
  padding: 2rem;
  border: 1px solid #e5e7eb;
  min-height: 200px;
}

.note-content {
  font-size: 1rem;
  line-height: 1.8;
  color: #1f2937;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
  color: #374151;
}

.section-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

/* Assignment Section */
.assignment-section {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border-color: #c4b5fd;
}

.assignment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.assignment-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: white;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid #e5e7eb;
}

.tag-exam { color: #7c3aed; border-color: #c4b5fd; }
.tag-subject { color: #2563eb; border-color: #bfdbfe; }
.tag-topic { color: #059669; border-color: #a7f3d0; }
.tag-lesson { color: #d97706; border-color: #fcd34d; }

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Dark Mode */
:global(body.dark-mode) .metadata-bar {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .translate-bar {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .translate-bar-left { color: #7dd3fc; }
:global(body.dark-mode) .translate-label { color: #7dd3fc; }
:global(body.dark-mode) .translate-select { background: #1e293b; border-color: #334155; color: #f3f4f6; }
:global(body.dark-mode) .translation-notice { background: #78350f; border-color: #b45309; color: #fde68a; }
:global(body.dark-mode) .translation-loading { background: #0c4a6e; border-color: #0369a1; color: #7dd3fc; }

:global(body.dark-mode) .metadata-label {
  color: #94a3b8;
}

:global(body.dark-mode) .content-section {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .main-content {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .note-content {
  color: #d1d5db;
}

:global(body.dark-mode) .section-header {
  color: #e5e7eb;
}

:global(body.dark-mode) .assignment-section {
  background: linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%);
  border-color: #7c3aed;
}

:global(body.dark-mode) .assignment-tag {
  background: #1e293b;
  border-color: #475569;
}

:global(body.dark-mode) .stat-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: #334155;
}

:global(body.dark-mode) .stat-value {
  color: #f3f4f6;
}

:global(body.dark-mode) .stat-label {
  color: #9ca3af;
}
</style>
